/**
 * Thin client for the backend (`/api/*`). Automatically attaches the current
 * Supabase access token so staff-only endpoints can authenticate.
 */
import type {
  AppetiteAnswer,
  GatekeeperCarrier,
  InternalNote,
  Message,
  SupplementalForm,
  FormAnswers,
  SubmissionData,
  SubmissionGrade,
} from '@commpass/shared';
import { supabase } from './supabase';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

/** One turn of a hub chat. `author` is a person at AAS, never a brand. */
export interface HubChatMessage {
  sender: 'member' | 'staff';
  author: string | null;
  body: string;
  at: string;
}

/**
 * A renewal account in the Inspector.
 *
 * ⚠️ `effective_date` IS THE EFFECTIVE DATE, NOT THE EXPIRY. The mock labels
 * this column "Expiry date"; the Inspector calls it "Effective date" on its own
 * create form and its own list, and it is what somebody typed there. On a
 * renewal tool those two are a policy term apart, and relabelling it would put a
 * date twelve months out under a heading people use to decide what is urgent.
 */
export interface InspectorAccount {
  id: string;
  org_id: string;
  created_at: string;
  updated_at: string;
  /** 'intake' | 'documents' | 'analyzing' | 'review' | 'complete' */
  status: string;
  named_insured: string | null;
  effective_date: string | null;
  created_by: string | null;
  data: {
    documents?: InspectorDoc[];
    lines?: string[];
    /** Present once the documents have been read. Generation refuses without it. */
    extraction?: unknown;
    artifacts?: InspectorArtifact[];
    executiveSummary?: string;
    /** What the model proposed. */
    coverageRecs?: InspectorRec[];
    /** What the agent chose — these two are what put rows on the signature page. */
    recommendedSelected?: string[];
    additionalSelected?: string[];
    /** Absent on every account created before sides existed — see sidesOf(). */
    sides?: InspectorSide[];
  } | null;
}

/**
 * One side of an account: a set of documents read on their own.
 *
 * ⚠️ THE PRIMARY SIDE'S EXTRACTION LIVES IN `data.extraction`, NOT HERE. That is
 * where the standalone Inspector reads it, so the two can never disagree about
 * the primary side.
 */
/**
 * One row in the Requests Center — a loss run, a reported loss, or a binder,
 * flattened to the shape they genuinely share.
 *
 * ⚠️ `urgency` IS THE ONLY COMPARABLE FIELD, AND `deadline` IS DELIBERATELY A
 * SENTENCE RATHER THAN A NUMBER. "Waiting 11 days" and "Incepts tomorrow" are
 * measured on different clocks; rendering them as one number would force a
 * comparison the data does not support.
 */
export interface UnifiedRequest {
  id: string;
  kind: 'lossrun' | 'fnol' | 'binder' | 'bor';
  kindLabel: string;
  href: string;
  insured: string;
  carrier: string;
  status: string;
  statusLabel: string;
  open: boolean;
  createdAt: string;
  waitingFor: string;
  deadline: string;
  urgency: 'late' | 'soon' | 'ok';
  /** A carrier answered and it is still open — the row that needs a person. */
  replied: boolean;
}

/**
 * One thing that has happened, for the notifications centre.
 *
 * ⚠️ DERIVED SERVER-SIDE FROM THE FACT THAT CAUSED IT — there is no
 * notifications table. So a notification cannot outlive or contradict its own
 * cause, and `at` is when the thing happened, never when it was noticed.
 */
export interface Notification {
  id: string;
  kind: 'reply' | 'overdue' | 'exhausted' | 'unmatched' | 'signed';
  title: string;
  detail: string;
  href: string;
  at: string;
  unread: boolean;
}

/** How long one carrier takes to answer, from requests already sent. */
export interface CarrierPerformance {
  carrier: string;
  sent: number;
  answered: number;
  open: number;
  /** Null when there are too few answers to say anything honest. */
  medianDays: number | null;
  meanDays: number | null;
  fastestDays: number | null;
  slowestDays: number | null;
  oldestOpenDays: number | null;
  thin: boolean;
}

/** One email in or out on a request. */
export interface RequestMessage {
  id: string;
  reference: string | null;
  direction: 'out' | 'in';
  from_email: string;
  from_name: string;
  to_emails: string[];
  subject: string;
  body_text: string;
  attachments: { name: string; mime: string; size: number; storagePath: string }[];
  created_at: string;
}

/**
 * A formal request to an underwriter to bind coverage.
 *
 * ⚠️ `effective_date` IS THE DEADLINE, not `created_at`. Everything that sorts
 * or colours this list keys off days-to-inception — a request sent this morning
 * for coverage incepting tomorrow outranks one sent three weeks ago.
 */
/**
 * One BOR/AOR request, as stored.
 *
 * ⚠️ TWO TIMESTAMPS, TWO WAITS. `signed_at` is the insured; `sent_to_carriers_at`
 * is us acting on it. A row with neither is waiting on a person outside the
 * agency, which is the state no other programme here has.
 */
export interface BorRequest {
  id: string;
  kind: 'bor' | 'aor';
  insured_name: string;
  signer_name: string;
  signer_email: string;
  agency_name: string;
  /** Carrier lines as stored on the row — jsonb, one per letter. */
  carriers: { carrier: string; policyNumbers?: string; to?: string[] }[];
  status: string;
  created_at: string;
  signed_at: string | null;
  sent_to_carriers_at: string | null;
  /** Why it ended the way it did. Blank until somebody closes it. */
  outcome_note?: string;
  closed_at?: string | null;
  /**
   * One row per carrier, each with its own clock.
   *
   * ⚠️ AFTER THE SIGNATURE THE REQUEST HOLDS NO VERDICT (Josh, 2026-09-14:
   * "the request is just a container"). Farmers releasing while Travelers
   * refuses is two facts, and any single word covering both is a label nobody
   * can act on — so anything asking "where is this?" asks the letters.
   */
  letters?: CarrierLetter[];
  /**
   * The signing token, so the page can show the link again.
   *
   * ⚠️ A SECRET, AND IT REACHES THE BROWSER ONLY BECAUSE THE LIST IS THE
   * REQUESTER'S OWN. Anything rendering it must stay behind the same check —
   * it is the whole authorisation to sign somebody's BOR.
   */
  sign_token?: string | null;
  last_resent_at?: string | null;
  resend_count?: number;
}

/**
 * One carrier's letter and its own retention window.
 *
 * ⚠️ 'retained' AND 'refused' ARE SEPARATE, AND COLLAPSING THEM WAS THE BUG.
 * One means the incumbent fought it and kept the account — the deal is gone.
 * The other means the carrier would not act on the letter, usually for
 * something fixable. Their follow-ups share nothing.
 */
export interface CarrierLetter {
  id: string;
  request_id: string;
  carrier: string;
  carrier_address: string;
  policy_numbers: string;
  policy_effective: string;
  to_emails: string[];
  status: 'pending' | 'sent' | 'released' | 'retained' | 'refused' | 'withdrawn';
  outcome_note: string;
  sent_at: string | null;
  replied_at: string | null;
  /** Calendar days. An estimate — we never see the carrier's own clock. */
  window_days: number;
  expect_by: string | null;
  /** The last step, and the one with money attached. */
  entered_ams_at: string | null;
}

/**
 * What an agent sees before releasing the letters.
 *
 * ⚠️ `wording` IS THE STORED COPY, NOT TODAY'S. Showing current wording beside
 * an older signature would show the agent something the signer never read.
 */
export interface BorReview {
  id: string;
  kind: 'bor' | 'aor';
  status: string;
  insuredName: string;
  signedAt: string | null;
  signature: {
    typedName: string;
    signedAt: string;
    wording: string;
    wordingVersion: string;
    ip: string;
    userAgent: string;
  } | null;
  /** Who was asked, so a wrong signer is visible beside what they typed. */
  askedOf: { name: string; email: string; title: string };
  letters: { carrier: string; to: string[]; text: string }[];
}

export interface BinderRequest {
  id: string;
  insured_name: string;
  carrier_name: string;
  quote_number: string;
  line_of_business: string;
  limits: string;
  deductible: string;
  premium: string;
  effective_date: string;
  effective_time: string;
  expiration_date: string | null;
  loss_payee: string;
  mailing_address: string;
  risk_address: string;
  notes: string;
  sent_to: string[];
  reply_to: string;
  status: 'sent' | 'chased' | 'bound' | 'declined' | 'closed';
  binder_number: string;
  bound_at: string | null;
  declined_reason: string;
  chase_count: number;
  created_at: string;
}

/** A coverage in the Inspector's catalogue. */
export interface InspectorCoverage {
  code: string;
  name: string;
  definition: string;
}

/**
 * The model's view of one coverage for this insured.
 *
 * ⚠️ A PROPOSAL, NOT A DECISION. A coverage reaches the document the insured
 * signs only because a person ticked it — `recommendedSelected` is what the PDF
 * reads, and it is written by the agent, not by this.
 */
export interface InspectorRec {
  code: string;
  priority: 'high' | 'medium' | 'low';
  rationale: string;
}

export interface InspectorSide {
  id: string;
  label: string;
  documentIds?: string[];
  extraction?: unknown;
  extractedAt?: number;
}

/**
 * Policy Comparison, as the server computed it.
 *
 * ⚠️ THESE MIRROR apps/api/src/inspector/compare.ts AND NOTHING ENFORCES THAT.
 * The extraction schemas are deliberately kept out of `packages/shared` so they
 * do not ship to the browser, which is the right call and leaves this pair of
 * types hand-maintained. Change one, change the other.
 *
 * ⚠️ `limits` IS FREE TEXT AND MUST NEVER BE PARSED HERE EITHER. The server
 * compares it for equality only; the UI shows it verbatim. Nothing on either
 * side of the wire is allowed to conclude that one limit is broader than
 * another — see the header of compare.ts for why.
 */
export interface ComparisonCell {
  carrier: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  /** Verbatim off the policy. Always shown; never parsed on this side. */
  limits: string;
  /** Parsed, when the extraction managed it. Null is common and stays readable. */
  occurrenceLimit: number | null;
  aggregateLimit: number | null;
  deductible: string;
  deductibleAmount: number | null;
  premium: number;
  premiumMissing: boolean;
}

export type LineVerdict =
  | 'missing'
  | 'carrier-changed'
  | 'limits-changed'
  | 'premium-changed'
  | 'limits-differ'
  | 'unchanged';

export interface LineComparison {
  key: string;
  name: string;
  /** One per side, in side order. Null means the line is absent on that side. */
  cells: (ComparisonCell | null)[];
  verdict: LineVerdict;
  premiumDelta: number | null;
  missingFrom: number[];
}

export interface ComparisonSide {
  id: string;
  label: string;
  isPrimary: boolean;
  extractedAt: number | null;
  totalPremium: number;
  /** Policies with no premium extracted. A total is not trustworthy without it. */
  premiumMissing: number;
  policyCount: number;
}

export interface Comparison {
  sides: ComparisonSide[];
  lines: LineComparison[];
  schedules: { label: string; counts: (number | null)[]; differs: boolean }[];
  notExtracted: string[];
  counts: { missing: number; changed: number; unchanged: number };
}

/** A generated file on an account — the workbook, or the client presentation. */
export interface InspectorArtifact {
  kind: 'workbook' | 'summary' | 'presentation';
  storagePath: string;
  fileName: string;
  at: number;
}

/** One file attached to an account, as the Inspector stores it in `data`. */
export interface InspectorDoc {
  id: string;
  /** 'policy' | 'loss_run' | 'application' | 'financials' | 'supporting' */
  kind: string;
  fileName: string;
  mime: string;
  /** Server-side only in practice — the browser never sends this back. */
  storagePath: string;
  size?: number;
  at: number;
}

/**
 * One policy on the Inspector's renewal work list.
 *
 * Field names are the Inspector's column names, deliberately unchanged — this
 * is another application's table and renaming on the way through would make the
 * two codebases describe the same row differently.
 */
export interface InspectorRenewal {
  id: string;
  org_id: string;
  policy_key: string;
  cust_id: string | null;
  customer_name: string;
  policy_number: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  parent_company: string | null;
  writing_company: string | null;
  primary_executive: string | null;
  primary_representative: string | null;
  policy_type_lob: string | null;
  lob_codes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  city: string | null;
  state: string | null;
  premium: number | null;
  /** 'full_term' | 'total_cost' | 'none' — 'none' means the number is unknown, not zero. */
  premium_source: string;
  account_id: string | null;
  dismissed: boolean;
}

export interface PortalSubmissionSummary {
  id: string;
  business_name: string | null;
  class_name: string | null;
  status: 'drafting' | 'sent' | 'withdrawn';
  sent_at: string | null;
  updated_at: string;
  grade: SubmissionGrade;
}

export interface PortalSubmission extends PortalSubmissionSummary {
  data: SubmissionData;
  response_id: string | null;
  grade_at_send: SubmissionGrade | null;
}

/** Listing shape from GET /api/forms — the full definition is far too large for a list. */
export interface FormSummary {
  class: string;
  fields: number;
  sections: number;
  audience: Record<string, number>;
  source: SupplementalForm['source'];
  /** False for an AI draft nobody at AAS has checked yet. */
  reviewed: boolean;
  extractedAt: string | null;
}

/** What the form collects. Shared by preview and send so they cannot drift. */
export interface LossRunInput {
  askedBy: 'insured' | 'agent_of_record' | 'new_agent' | 'aas';
  insuredName: string;
  policyNumber: string;
  policyPeriod: string;
  linesOfCoverage: string[];
  yearsWanted: string;
  requesterName: string;
  requesterTitle: string;
  requesterPhone: string;
  agencyName: string;
  carrierName: string;
  sendTo: string[];
  replyTo: string;
  notes: string;
  /**
   * Ask the carrier for a quicker turnaround.
   *
   * ⚠️ IT REWRITES ONE SENTENCE OF THE LETTER, SERVER-SIDE. The form never
   * composes letter prose — that is what keeps the preview and the sent email
   * the same text rather than two renderings that can disagree.
   */
  urgent: boolean;
  /** YYYY-MM-DD, or empty. Only meaningful when `urgent`. */
  neededBy: string;
  files: { name: string; mime: string; data: string; size?: number }[];
  /** The letter as edited on the review step. Omitted means "use the standard one". */
  bodyText?: string;
  subject?: string;
}

export type LossType = 'property' | 'auto' | 'general-liability' | 'workers-comp' | 'other';

/** What the loss notice form collects. Shared by preview and send. */
export interface FnolInput {
  insuredName: string;
  policyNumber: string;
  carrierName: string;
  lossType: LossType;
  lossDate: string;
  lossTime: string;
  lossLocation: string;
  description: string;
  contactName: string;
  contactPhone: string;
  injuries: boolean;
  authorities: string;
  reporterName: string;
  agencyName: string;
  reporterPhone: string;
  sendTo: string[];
  replyTo: string;
  files: { name: string; mime: string; data: string; size?: number }[];
  bodyText?: string;
  subject?: string;
}

/** One loss notice, as the list shows it. Mirrors fnol_reports. */
export interface FnolReport {
  id: string;
  reporter_email: string;
  insured_name: string;
  policy_number: string;
  carrier_name: string;
  loss_type: string;
  loss_date: string;
  description: string;
  sent_to: string[];
  reply_to: string;
  status: 'sent' | 'chased' | 'acknowledged' | 'closed';
  chase_count: number;
  acknowledged_at: string | null;
  claim_number: string;
  created_at: string;
}

/** One loss run request, as the list shows it. Mirrors loss_run_requests. */
export interface LossRunRequest {
  id: string;
  requester_email: string;
  asked_by: 'insured' | 'agent_of_record' | 'new_agent' | 'aas';
  insured_name: string;
  policy_number: string;
  years_wanted: string;
  carrier_name: string;
  sent_to: string[];
  reply_to: string;
  authorization_attached: boolean;
  status: 'sent' | 'chased' | 'received' | 'closed';
  chase_count: number;
  last_chased_at: string | null;
  received_at: string | null;
  created_at: string;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, opts: RequestInit = {}, auth = false): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set('Content-Type', 'application/json');

  if (auth) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : undefined;

  if (!res.ok) {
    const msg = (body && (body.error as string)) || `Request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

// ---- Public (agent) endpoints ----
export const api = {
  createSubmission(input: { data: unknown; pin: string; turnstileToken?: string }) {
    return request<{ id: string }>('/api/public/submissions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // ---- Commercial Hub (authenticated) ----
  hubProvisionAgency(input: { memberId: string; adminEmail: string; adminName?: string }) {
    return request<{ ok: true; emailed: boolean; signInLink: string | null }>(
      '/api/hub/agencies/provision',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  hubAddTeamMember(input: { memberId: string; email: string; fullName?: string; role?: string }) {
    return request<{ ok: true; emailed: boolean; signInLink: string | null }>(
      '/api/hub/team/add',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  /**
   * Ask Connor about appetite. Authenticated: the answer names the markets AAS is
   * appointed with, which is what a member's hub access is for.
   */
  appetiteAsk(question: string) {
    return request<AppetiteAnswer>(
      '/api/appetite/ask',
      { method: 'POST', body: JSON.stringify({ question }) },
      true,
    );
  },

  /**
   * Tell us how a quote went. The only signal we ever get back about whether
   * the guidance was any good — see the note on the route.
   */
  appetiteOutcome(input: { question: string; className: string | null; outcome: string; note?: string }) {
    return request<{ ok: true }>('/api/appetite/outcome', { method: 'POST', body: JSON.stringify(input) }, true);
  },

  /**
   * "Which markets can I get Kinsale through?" — answered from our own book,
   * so these are markets we have genuinely placed that paper through.
   */
  appetitePaper(q: string) {
    return request<{ results: { paper: string; markets: { name: string; n: number }[] }[]; asOf: string }>(
      `/api/appetite/paper?q=${encodeURIComponent(q)}`,
      {},
      true,
    );
  },

  /**
   * Read uploaded dec pages and return the policy fields found in them.
   *
   * One entry per distinct policy, not per file — a binder and a dec page for
   * the same policy are one entry. Blank strings mean "not written in the
   * documents", never "we could not be bothered": the caller must treat them as
   * fields the member still has to fill.
   */
  policyUploadExtract(files: { name: string; mime: string; data: string }[], linkToken?: string) {
    // The public link has no session, so it identifies itself by token instead.
    return linkToken
      ? request<{ policies: Record<string, string>[]; read: number; skipped?: string[] }>(
          '/api/public/policy-upload/extract',
          { method: 'POST', body: JSON.stringify({ files, token: linkToken }) },
        )
      : request<{ policies: Record<string, string>[]; read: number; skipped?: string[] }>(
          '/api/policy-upload/extract',
          { method: 'POST', body: JSON.stringify({ files }) },
          true,
        );
  },

  /** Is the standalone Iron Peak link still switched on? */
  policyUploadLinkLive(token: string) {
    return request<{ ok: true }>(`/api/public/policy-upload/${encodeURIComponent(token)}`, { method: 'GET' });
  },

  /** Send through the standalone link — identity comes from the form, not a session. */
  policyUploadPublic(input: {
    token: string;
    senderName: string;
    senderEmail: string;
    agencyName: string;
    bill: 'direct' | 'agency';
    rows: Record<string, string>[];
    files: { name: string; mime: string; data: string; size: number }[];
    turnstileToken?: string;
  }) {
    return request<{ ok: true; reference: string; attachments: number; skipped: string[] }>(
      '/api/public/policy-upload',
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  /**
   * Send a policy to Agents Alliance to be keyed into AMS360. Iron Peak only —
   * the route 404s for anyone without the module.
   */
  policyUpload(input: {
    bill: 'direct' | 'agency';
    rows: Record<string, string>[];
    files: { name: string; mime: string; data: string; size: number }[];
  }) {
    return request<{ ok: true; reference: string; attachments: number; skipped: string[] }>(
      '/api/policy-upload',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  // ---- Loss runs ----
  /**
   * Who to ask at a carrier, best desk first. Comes from the Salesforce contact
   * book, baked at build time — an empty list means we hold nobody for that
   * carrier, not that the lookup failed.
   */
  /**
   * `purpose` decides the ranking: a claims desk for a loss notice, a loss run
   * desk for a records request. FNOL has no underwriter fallback — see RANKING
   * on the server.
   */
  carrierContacts(carrier: string, purpose: 'lossrun' | 'claims' = 'lossrun') {
    return request<{
      carriers: { name: string; hasContact: boolean }[];
      matched?: string | null;
      contacts: { name: string; email: string; role: string }[];
    }>(
      `/api/loss-runs/contacts?carrier=${encodeURIComponent(carrier)}&purpose=${purpose}`,
      {},
      true,
    );
  },

  lossRunContacts(carrier: string) {
    return request<{
      /** Every carrier matching what was typed, flagged with whether we hold anyone. */
      carriers: { name: string; hasContact: boolean }[];
      matched?: string | null;
      contacts: { name: string; email: string; role: string }[];
    }>(`/api/loss-runs/contacts?carrier=${encodeURIComponent(carrier)}`, {}, true);
  },

  /**
   * Everything you are waiting on a carrier for, across Loss Runs, Report a Loss
   * and Binder Requests.
   *
   * ⚠️ URGENCY IS COMPUTED SERVER-SIDE AND ARRIVES DECIDED. Each programme has
   * its own clock — days waited, days unacknowledged, days to inception — and
   * re-deriving any of them here would be a second opinion that drifts from that
   * programme's own page the first time a threshold changes.
   */
  requestsAll() {
    return request<{ requests: UnifiedRequest[] }>('/api/requests', {}, true);
  },

  requestMessages(reference: string) {
    return request<{ reference: string; messages: RequestMessage[] }>(
      `/api/requests/${encodeURIComponent(reference)}/messages`,
      {},
      true,
    );
  },

  /** A short-lived link to one file off a thread. The path never leaves the server. */
  requestAttachmentUrl(reference: string, messageId: string, index: number) {
    return request<{ url: string; fileName: string }>(
      `/api/requests/${encodeURIComponent(reference)}/messages/${encodeURIComponent(messageId)}/attachments/${index}/url`,
      {},
      true,
    );
  },

  /** The triage queue: replies that arrived with no reference. Staff only. */
  unmatchedMessages() {
    return request<{ messages: RequestMessage[] }>('/api/requests/unmatched', {}, true);
  },

  /** File an unmatched message against a request, or set it aside with no reference. */
  fileUnmatched(id: string, reference?: string) {
    return request<{ ok: true; reference: string | null }>(
      `/api/requests/unmatched/${encodeURIComponent(id)}`,
      { method: 'POST', body: JSON.stringify({ reference: reference ?? '' }) },
      true,
    );
  },

  notifications() {
    return request<{ notifications: Notification[]; unread: number }>('/api/notifications', {}, true);
  },

  /** "I have looked." Moves one timestamp; deletes nothing. */
  notificationsSeen() {
    return request<{ ok: boolean }>('/api/notifications/seen', { method: 'POST' }, true);
  },

  carrierPerformance() {
    return request<{
      carriers: CarrierPerformance[];
      scope: 'alliance' | 'you';
      minSample: number;
      answered: number;
    }>('/api/carriers/performance', {}, true);
  },

  binderLines() {
    return request<{ lines: { code: string; name: string; limits: string[] }[] }>(
      '/api/binders/lines',
      {},
      true,
    );
  },

  binderCarriers() {
    return request<{ carriers: string[]; withContacts: number }>('/api/binders/carriers', {}, true);
  },

  binderContacts(carrier: string) {
    return request<{ carrier: string | null; contacts: { name: string; email: string; role: string }[] }>(
      `/api/binders/contacts?carrier=${encodeURIComponent(carrier)}`,
      {},
      true,
    );
  },

  /** Every BOR/AOR request this member has raised. */
  borList() {
    return request<{ requests: BorRequest[] }>('/api/bor', {}, true);
  },

  /** The letter for the first carrier, before anybody is emailed. */
  borPreview(input: Record<string, unknown>) {
    return request<{ letter: string; affirmations: { identity: string; authority: string } }>(
      '/api/bor/preview',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  /**
   * Create the request and email the insured a signing link.
   *
   * Nothing reaches a carrier here — an unsigned BOR is not a BOR. The only
   * person emailed is the signer.
   */
  borCreate(input: Record<string, unknown>) {
    return request<{ id: string; status: string; signUrl: string }>(
      '/api/bor',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  /**
   * Logos found on the insured's website, for the agent to choose between.
   *
   * Candidates only — nothing is applied until somebody clicks one. An empty
   * list is the ordinary answer for a site with no usable logo, and is not
   * distinguished from a site we could not read.
   */
  /**
   * Everything needed to judge a signature before the letters go.
   *
   * The signature is the point, not the letter — the agent wrote the letter.
   * What they have never seen is who signed, what they typed and what they
   * affirmed.
   */
  borReview(id: string) {
    return request<BorReview>(`/api/bor/${encodeURIComponent(id)}/review`, {}, true);
  },

  /**
   * Send the signed letters to the carriers. The irreversible step.
   *
   * Once a carrier has it, the incumbent agent is notified and their window to
   * save the account starts. There is no recall.
   */
  borSend(id: string) {
    return request<{ delivered: number; carriers: number; failed: string[] }>(
      `/api/bor/${encodeURIComponent(id)}/send`,
      { method: 'POST' },
      true,
    );
  },

  /**
   * Send the signing link again, optionally to a corrected address.
   *
   * Reuses the existing token unless it has expired — the signer may be about
   * to click the link they already have.
   */
  borResend(id: string, email: string) {
    return request<{ ok: true; sentTo: string; newLink: boolean }>(
      `/api/bor/${encodeURIComponent(id)}/resend`,
      { method: 'POST', body: JSON.stringify({ email }) },
      true,
    );
  },

  /**
   * Close a request — the carrier honoured it, refused it, or we pulled it.
   *
   * Withdrawing also kills the signing link, which is the reason it is a call
   * to the server rather than a local status change.
   */
  /** Record what ONE carrier decided. Per letter — the request holds no verdict. */
  borLetterOutcome(
    letterId: string,
    status: 'released' | 'retained' | 'refused' | 'withdrawn',
    note: string,
  ) {
    return request<{ id: string; status: string }>(
      `/api/bor/letters/${encodeURIComponent(letterId)}/outcome`,
      { method: 'POST', body: JSON.stringify({ status, note }) },
      true,
    );
  },

  /** "It is in AMS360 now" — the step that closes a released letter for good. */
  borLetterEntered(letterId: string) {
    return request<{ id: string; enteredAmsAt: string }>(
      `/api/bor/letters/${encodeURIComponent(letterId)}/entered`,
      { method: 'POST' },
      true,
    );
  },

  /** Withdraw the whole request before anybody signs. Kills the signing link. */
  borWithdraw(id: string, note: string) {
    return request<{ id: string; status: string }>(
      `/api/bor/${encodeURIComponent(id)}/withdraw`,
      { method: 'POST', body: JSON.stringify({ note }) },
      true,
    );
  },

  borLogoLookup(website: string) {
    return request<{
      candidates: { source: string; dataUrl: string; mime: string; label: string }[];
    }>('/api/bor/logo-lookup', { method: 'POST', body: JSON.stringify({ website }) }, true);
  },

  binderList() {
    return request<{ requests: BinderRequest[] }>('/api/binders', {}, true);
  },

  binderPreview(input: Record<string, unknown>) {
    return request<{ subject: string; bodyText: string }>(
      '/api/binders/preview',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  binderSend(input: Record<string, unknown>) {
    return request<{ ok: boolean; id: string; from: string }>(
      '/api/binders',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  binderBound(id: string, binderNumber: string) {
    return request<{ ok: boolean }>(
      `/api/binders/${encodeURIComponent(id)}/bound`,
      { method: 'POST', body: JSON.stringify({ binderNumber }) },
      true,
    );
  },

  binderClose(id: string, status: 'declined' | 'closed', reason = '') {
    return request<{ ok: boolean }>(
      `/api/binders/${encodeURIComponent(id)}/close`,
      { method: 'POST', body: JSON.stringify({ status, reason }) },
      true,
    );
  },

  lossRunList() {
    return request<{ requests: LossRunRequest[] }>('/api/loss-runs', {}, true);
  },

  /**
   * Send the request. One email per address, so `ok` can be false while
   * `sentTo` is non-empty — read both.
   *
   * `authorizationExpected` is true when a new agent asked and attached nothing.
   * The request went anyway; it is a warning, not a failure.
   */
  /**
   * The letter exactly as it will be sent — rendered by the same function that
   * sends it, so a preview cannot be right while the email is wrong.
   */
  lossRunPreview(input: Omit<LossRunInput, 'sendTo' | 'carrierName' | 'files'> & { attachmentCount: number }) {
    return request<{ subject: string; bodyText: string }>(
      '/api/loss-runs/preview',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  lossRunCreate(input: LossRunInput) {
    return request<{
      ok: boolean;
      id: string;
      sentTo: string[];
      failed: string[];
      skipped: string[];
      tracked: boolean;
      authorizationExpected?: boolean;
    }>('/api/loss-runs', { method: 'POST', body: JSON.stringify(input) }, true);
  },

  lossRunChase(id: string) {
    return request<{ ok: true; sentTo?: string[]; chaseCount?: number; skipped?: string }>(
      `/api/loss-runs/${encodeURIComponent(id)}/chase`,
      { method: 'POST', body: '{}' },
      true,
    );
  },

  lossRunResolve(id: string, outcome: 'received' | 'closed', reason = '') {
    return request<{ ok: true }>(
      `/api/loss-runs/${encodeURIComponent(id)}/resolve`,
      { method: 'POST', body: JSON.stringify({ outcome, reason }) },
      true,
    );
  },

  // ---- First notice of loss ----
  fnolPreview(input: Omit<FnolInput, 'sendTo' | 'carrierName' | 'files'>) {
    return request<{ subject: string; bodyText: string }>(
      '/api/fnol/preview',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  fnolList() {
    return request<{ reports: FnolReport[] }>('/api/fnol', {}, true);
  },

  /**
   * The Account Inspector's renewal work list.
   *
   * ⚠️ THESE ROWS COME FROM THE INSPECTOR'S OWN DATABASE, VIA THE HUB API. The
   * standalone Inspector queries Supabase straight from the browser under RLS;
   * doing that here would need a second sign-in in the hub's browser, so the
   * read goes through the server instead. Nothing on this side holds an
   * Inspector session.
   */
  inspectorMe() {
    return request<{ configured: boolean; orgs: { id: string; name: string }[]; staff: boolean }>(
      '/api/inspector/me',
      {},
      true,
    );
  },

  inspectorAccounts() {
    return request<{ accounts: InspectorAccount[]; orgs: string[] }>('/api/inspector/accounts', {}, true);
  },

  inspectorCreateAccount(input: { namedInsured: string; effectiveDate?: string }) {
    return request<{ account: InspectorAccount }>(
      '/api/inspector/accounts',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  inspectorAccount(id: string) {
    return request<{ account: InspectorAccount }>(`/api/inspector/accounts/${encodeURIComponent(id)}`, {}, true);
  },

  inspectorSetStatus(id: string, status: string) {
    return request<{ account: InspectorAccount }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
      true,
    );
  },

  /**
   * A short-lived link to one document.
   *
   * ⚠️ THE SERVER SIGNS IT FROM THE DOCUMENT ID, and the browser never sends a
   * storage path — see the route. Two minutes is deliberately short: these are
   * policies and loss runs, and a link that outlives the click is a link that
   * outlives the session.
   */
  inspectorDocUrl(id: string, docId: string) {
    return request<{ url: string; fileName: string }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/url`,
      {},
      true,
    );
  },

  inspectorUploadDoc(
    id: string,
    input: { kind: string; fileName: string; mime: string; contentBase64: string },
  ) {
    return request<{ account: InspectorAccount; document: { id: string } }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/documents`,
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  /**
   * Read the account's documents with Claude into structured policy and loss data.
   *
   * ⚠️ SLOW BY NATURE — it sends whole PDFs to the model and asks for up to
   * 16,000 tokens back. Callers must show that something is happening; a button
   * that looks dead for ninety seconds gets pressed again.
   */
  inspectorExtract(accountId: string, sideId?: string) {
    return request<{ extraction: unknown; lines: string[]; docsRead: number; skipped: string[] }>(
      '/api/inspector/ai/extract',
      { method: 'POST', body: JSON.stringify({ accountId, sideId }) },
      true,
    );
  },

  /**
   * The coverage catalogue, served rather than bundled — it is the same list the
   * AI prompt and the printed glossary read, and a copy in the browser would
   * drift from the definitions on the document a client signs.
   */
  inspectorCoverages() {
    return request<{
      gaps: InspectorCoverage[];
      additional: { key: string; label: string; items: InspectorCoverage[] }[];
    }>('/api/inspector/coverages', {}, true);
  },

  /** Ask the model which gaps matter for this insured, and why. Slow — it thinks. */
  inspectorRecommend(accountId: string) {
    return request<{ recommendations: InspectorRec[] }>(
      '/api/inspector/ai/recommendations',
      { method: 'POST', body: JSON.stringify({ accountId }) },
      true,
    );
  },

  inspectorSetCoverages(
    id: string,
    sel: { recommendedSelected?: string[]; additionalSelected?: string[] },
  ) {
    return request<{ account: InspectorAccount }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/coverages`,
      { method: 'PATCH', body: JSON.stringify(sel) },
      true,
    );
  },

  inspectorAddSide(id: string, label: string) {
    return request<{ account: InspectorAccount; sideId: string }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/sides`,
      { method: 'POST', body: JSON.stringify({ label }) },
      true,
    );
  },

  /** Rename a side. The label moves; documents and any extraction do not. */
  inspectorRenameSide(id: string, sideId: string, label: string) {
    return request<{ account: InspectorAccount }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/sides/${encodeURIComponent(sideId)}`,
      { method: 'PATCH', body: JSON.stringify({ label }) },
      true,
    );
  },

  inspectorMoveDoc(id: string, docId: string, sideId: string) {
    return request<{ account: InspectorAccount }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/side`,
      { method: 'PATCH', body: JSON.stringify({ sideId }) },
      true,
    );
  },

  /** `comparison` is null when fewer than two sides have been read — not an error. */
  inspectorComparison(id: string) {
    return request<{ comparison: Comparison | null }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/comparison`,
      {},
      true,
    );
  },

  inspectorGenerate(accountId: string, reuseNarrative = false) {
    return request<{
      metrics: unknown;
      flags: unknown[];
      executiveSummary: string;
      artifacts: { kind: string; fileName: string; at: number }[];
    }>('/api/inspector/generate', { method: 'POST', body: JSON.stringify({ accountId, reuseNarrative }) }, true);
  },

  inspectorArtifactUrl(id: string, kind: string) {
    return request<{ url: string; fileName: string }>(
      `/api/inspector/accounts/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(kind)}/url`,
      {},
      true,
    );
  },

  inspectorRenewals() {
    return request<{ rows: InspectorRenewal[]; orgs: string[] }>('/api/inspector/renewals', {}, true);
  },

  fnolCreate(input: FnolInput) {
    return request<{
      ok: boolean;
      id: string;
      sentTo: string[];
      failed: string[];
      skipped: string[];
      tracked: boolean;
    }>('/api/fnol', { method: 'POST', body: JSON.stringify(input) }, true);
  },

  /** The acknowledgment — the artifact that proves the carrier has it. */
  fnolAcknowledge(id: string, claimNumber: string) {
    return request<{ ok: true }>(
      `/api/fnol/${encodeURIComponent(id)}/acknowledge`,
      { method: 'POST', body: JSON.stringify({ claimNumber }) },
      true,
    );
  },

  fnolChase(id: string) {
    return request<{ ok: true; sentTo?: string[]; chaseCount?: number; skipped?: string }>(
      `/api/fnol/${encodeURIComponent(id)}/chase`,
      { method: 'POST', body: '{}' },
      true,
    );
  },

  fnolClose(id: string, reason = '') {
    return request<{ ok: true }>(
      `/api/fnol/${encodeURIComponent(id)}/close`,
      { method: 'POST', body: JSON.stringify({ reason }) },
      true,
    );
  },

  /** A class's supplemental questionnaire definition. */
  getForm(className: string) {
    return request<SupplementalForm>(`/api/forms/${encodeURIComponent(className)}`, {}, true);
  },

  /** Which classes have a questionnaire; staff also see unreviewed drafts. */
  listForms() {
    return request<{ forms: FormSummary[]; staffView: boolean }>('/api/forms', {}, true);
  },

  // ---- Filled-in questionnaires ----
  createResponse(className: string, businessName?: string) {
    return request<{ id: string }>(
      `/api/forms/${encodeURIComponent(className)}/responses`,
      { method: 'POST', body: JSON.stringify({ businessName }) },
      true,
    );
  },

  saveResponse(id: string, input: { answers?: FormAnswers; businessName?: string }) {
    return request<{ ok: true; savedAt: string }>(
      `/api/responses/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      true,
    );
  },

  getResponse(id: string) {
    return request<{ id: string; class_name: string; status: string; business_name: string | null; answers: FormAnswers }>(
      `/api/responses/${encodeURIComponent(id)}`,
      {},
      true,
    );
  },

  /** Mint the insured's link and PIN. The PIN is shown once and never again. */
  inviteInsured(id: string, insuredEmail?: string) {
    return request<{ ok: true; link: string; pin: string; expiresAt: string }>(
      `/api/responses/${encodeURIComponent(id)}/invite`,
      { method: 'POST', body: JSON.stringify({ insuredEmail }) },
      true,
    );
  },

  // ---- Submission Portal (portal_submissions; never touches COMM-PASS) ----
  portalCreate(input: { businessName?: string; className?: string; responseId?: string }) {
    return request<{ id: string }>('/api/portal/submissions', { method: 'POST', body: JSON.stringify(input) }, true);
  },

  /** Staff-only: what members have actually sent. Drafts are excluded. */
  portalQueue() {
    return request<{
      submissions: {
        id: string;
        businessName: string | null;
        className: string | null;
        agentEmail: string;
        sentAt: string | null;
        hasQuestionnaire: boolean;
        lines: string[];
        documentCount: number;
        grade: SubmissionGrade;
        gradeNow: SubmissionGrade;
      }[];
    }>('/api/portal/queue', {}, true);
  },

  /** What this person may open. An empty list means the hub isn't theirs. */
  hubMe() {
    return request<{
      signedIn: boolean;
      isStaff: boolean;
      email?: string;
      modules: string[];
      /**
       * The one module this person is restricted to, or null/absent.
       *
       * ⚠️ NEVER INFER THIS FROM modules.length === 1. The server says it
       * explicitly for a reason — see the note on /api/hub/me. Treating a
       * one-module list as "restricted" would silently unrestrict someone the
       * day a defaultOn flag changed.
       */
      pinned?: string | null;
    }>('/api/hub/me', {}, true);
  },

  portalList() {
    return request<{ submissions: PortalSubmissionSummary[] }>('/api/portal/submissions', {}, true);
  },

  portalGet(id: string) {
    return request<PortalSubmission>(`/api/portal/submissions/${encodeURIComponent(id)}`, {}, true);
  },

  /**
   * Read the attached documents and the company website, and come back with a
   * description of operations and an executive summary. Returns the text; the
   * caller decides whether to keep it.
   */
  portalNarrative(id: string) {
    return request<{
      descriptionOfOps: string;
      executiveSummary: string;
      unreadable: boolean;
      /** Raw extractor keys — see EXTRACT_TO_FIELD in SubmissionBuild. */
      fields: Record<string, string>;
    }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/narrative`,
      // ⚠️ The empty object is required, not decoration. request() always sets
      // Content-Type: application/json, and Fastify rejects a POST that
      // declares JSON and sends nothing with a 400 — which is what "bad
      // request" was when this button was first pressed.
      { method: 'POST', body: JSON.stringify({}) },
      true,
    );
  },

  /**
   * Ask a person at Agents Alliance. Goes to a Teams group chat; the answer
   * comes back into the same conversation.
   *
   * ⚠️ READ `notified`. False means the message was stored but nobody at AAS
   * was paged — the flow is down or not configured. The member has to be told,
   * because from their side a delivered question and a swallowed one look
   * identical until the silence goes on too long.
   */
  hubChatSend(text: string, context?: Record<string, string>) {
    return request<{ threadId: string; notified: boolean; messages: HubChatMessage[] }>(
      '/api/hub/chat',
      { method: 'POST', body: JSON.stringify({ text, context }) },
      true,
    );
  },

  /**
   * Is the conversational Connor switched on for this person?
   *
   * Two locks server-side — a flag and the hub-staff allow-list — and this
   * reports the AND of them, so the client never has to probe an endpoint that
   * 404s by design.
   */
  connorAvailable() {
    return request<{ available: boolean }>('/api/connor/available', { method: 'GET' }, true);
  },

  /** One conversational turn. History is sent whole and truncated server-side. */
  connorChat(messages: { role: 'user' | 'assistant'; content: string }[]) {
    return request<{
      reply: string;
      actions: (
        | { type: 'openPage'; page: string; id?: string }
        /** A draft for the desk. It has NOT been sent — the member presses send. */
        | { type: 'askDesk'; message: string }
      )[];
      toolsUsed: string[];
      truncated: boolean;
      /** A lookup ran and came back empty — gates the offer of a person. */
      unresolved?: boolean;
    }>('/api/connor/chat', { method: 'POST', body: JSON.stringify({ messages }) }, true);
  },

  /** One conversation in full, for reading and printing. Owner or staff. */
  hubChatThread(id: string) {
    return request<{
      thread: {
        id: string;
        status: string;
        claimed_by: string | null;
        agency_name: string | null;
        agent_email: string;
        created_at: string;
        last_message_at: string;
      };
      messages: HubChatMessage[];
    }>(`/api/hub/chat/thread/${encodeURIComponent(id)}`, { method: 'GET' }, true);
  },

  /** Every conversation this person has had, newest first. */
  hubChatHistory() {
    return request<{
      threads: {
        id: string;
        status: string;
        claimed_by: string | null;
        created_at: string;
        last_message_at: string;
      }[];
    }>('/api/hub/chat/history', { method: 'GET' }, true);
  },

  /** Poll the conversation. Runs every few seconds while the chat is open. */
  hubChatPoll() {
    return request<{
      threadId: string | null;
      status?: string;
      claimedBy: string | null;
      messages: HubChatMessage[];
    }>('/api/hub/chat', { method: 'GET' }, true);
  },

  /**
   * Send the submission to underwriters. The server does the sending, with
   * Reply-To set to the agent so the answer comes back to them.
   *
   * One email per address, so `ok` can be false while `sentTo` is non-empty —
   * some markets got it and some did not. Read both.
   */
  portalEmail(id: string, input: { to: string[]; subject: string; text: string; coverPdf?: string; coverName?: string }) {
    return request<{ ok: boolean; sentTo: string[]; failed: string[]; attachments: number; skipped: string[]; grade: SubmissionGrade }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/email`,
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  /** Saves and returns the freshly computed grade — the server owns the rubric. */
  portalSave(id: string, input: { data?: Record<string, unknown>; businessName?: string; className?: string; responseId?: string }) {
    return request<{ ok: true; grade: SubmissionGrade }>(
      `/api/portal/submissions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      true,
    );
  },

  /** Attach a file. Base64 in the body, matching how COMM-PASS already uploads. */
  /**
   * Attach a file. `kind: 'auto'` asks the server to work out what it is; an
   * explicit kind is a member correcting that guess. A zip is unpacked server
   * side, so one upload can add several documents — hence `added` rather than
   * a single path.
   */
  portalUpload(id: string, input: { kind: string; fileName: string; mime: string; data: string }) {
    return request<{
      ok: true;
      added: { storagePath: string; kind: string; kindBy: string; label: string }[];
      skipped: string[];
      grade: SubmissionGrade;
    }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/documents`,
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  portalRemoveDoc(id: string, storagePath: string) {
    return request<{ ok: true; grade: SubmissionGrade }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/documents?path=${encodeURIComponent(storagePath)}`,
      { method: 'DELETE' },
      true,
    );
  },

  /** A signed URL, good for five minutes — the bucket is private. */
  portalDocUrl(id: string, storagePath: string) {
    return request<{ url: string }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/documents/url?path=${encodeURIComponent(storagePath)}`,
      {},
      true,
    );
  },

  portalSend(id: string) {
    return request<{ ok: true; id: string; grade: SubmissionGrade }>(
      `/api/portal/submissions/${encodeURIComponent(id)}/send`,
      { method: 'POST', body: '{}' },
      true,
    );
  },

  // ---- Insured side: no account, token + PIN on every call ----
  insuredOpen(token: string, pin: string) {
    return request<{
      businessName: string | null;
      className: string;
      status: string;
      form: SupplementalForm;
      answers: FormAnswers;
    }>('/api/insured/open', { method: 'POST', body: JSON.stringify({ token, pin }) });
  },

  insuredSave(token: string, pin: string, answers: FormAnswers) {
    return request<{ ok: true }>('/api/insured/save', {
      method: 'PATCH',
      body: JSON.stringify({ token, pin, answers }),
    });
  },

  insuredComplete(token: string, pin: string) {
    return request<{ ok: true }>('/api/insured/complete', {
      method: 'POST',
      body: JSON.stringify({ token, pin }),
    });
  },

  hubSignInLink(input: { email: string }) {
    return request<{ ok: true; signInLink: string }>(
      '/api/hub/team/sign-in-link',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  listMembers() {
    return request<{ members: { member_id: string; name: string; account_id: string | null }[] }>(
      '/api/public/members',
    );
  },

  retrieveSubmission(input: { code: string; pin: string }) {
    return request<{ id: string; status: string; data: Record<string, unknown> }>(
      '/api/public/submissions/retrieve',
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  uploadDoc(input: {
    submissionCode?: string;
    fileName: string;
    mime: string;
    data: string;
    turnstileToken?: string;
  }) {
    return request<{ storagePath: string; fileName: string }>('/api/public/uploads', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // ---- Staff endpoints (require auth) ----
  // Gatekeeper underwriter contacts — staff only; never in the browser bundle.
  getGatekeeperCarriers() {
    return request<{ carriers: GatekeeperCarrier[] }>('/api/staff/gatekeeper-carriers', {}, true);
  },

  // Staff: attach a document directly to a submission (emailed-in files, an
  // unzipped bundle, etc.). Stored + appended like an agent upload.
  staffUploadDoc(input: { submissionId: string; fileName: string; mime: string; data: string }) {
    return request<{ document: { id: string; label: string; storagePath: string; fileName: string } }>(
      '/api/staff/upload-doc',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  // Staff: reset a submission's login PIN. Returns the new 4-digit PIN once, to relay
  // to the agent. The old PIN stops working; the retrieval code is unchanged.
  resetPin(submissionId: string) {
    return request<{ pin: string }>(
      '/api/staff/reset-pin',
      { method: 'POST', body: JSON.stringify({ submissionId }) },
      true,
    );
  },

  aiExtract(files: { mime: string; data: string; name?: string }[]) {
    return request<{ extracted: Record<string, unknown> }>(
      '/api/ai/extract',
      { method: 'POST', body: JSON.stringify({ files }) },
      true,
    );
  },

  aiExecSummary(facts: string) {
    return request<{ summary: string }>(
      '/api/ai/exec-summary',
      { method: 'POST', body: JSON.stringify({ facts }) },
      true,
    );
  },

  aiNarrative(submissionId: string) {
    return request<{
      descriptionOfOps: string;
      executiveSummary: string;
      unreadable: boolean;
      verification?: { claim: string; note: string }[];
    }>(
      '/api/ai/narrative',
      { method: 'POST', body: JSON.stringify({ submissionId }) },
      true,
    );
  },

  syncSalesforce(kind: 'submission' | 'solution', id: string) {
    return request<{ ok: true }>(
      '/api/sync/salesforce',
      { method: 'POST', body: JSON.stringify({ kind, id }) },
      true,
    );
  },

  notifyProposalSent(submissionId: string) {
    return request<{ ok: true; emailed: boolean }>(
      '/api/staff/proposal-notify',
      { method: 'POST', body: JSON.stringify({ submissionId }) },
      true,
    );
  },

  // ---- Messaging ----
  postStaffMessage(submissionId: string, text: string) {
    return request<{ messages: Message[] }>(
      '/api/staff/messages',
      { method: 'POST', body: JSON.stringify({ submissionId, text }) },
      true,
    );
  },

  // Staff-only internal note (never shown to agents).
  addInternalNote(submissionId: string, text: string) {
    return request<{ internalNotes: InternalNote[] }>(
      '/api/staff/internal-note',
      { method: 'POST', body: JSON.stringify({ submissionId, text }) },
      true,
    );
  },

  postAgentMessage(input: { code: string; pin: string; text: string }) {
    return request<{ messages: Message[] }>('/api/public/messages', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  downloadProposal(input: { code: string; pin: string }) {
    return request<{ url: string }>('/api/public/proposal-download', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  requestDocumentNotify(input: { submissionId: string; labels: string[]; note?: string }) {
    return request<{ ok: true; emailed: boolean }>(
      '/api/staff/request-document-notify',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },

  uploadRequestedDoc(input: {
    code: string;
    pin: string;
    requestId: string;
    fileName: string;
    mime: string;
    data: string;
  }) {
    return request<{ ok: true }>('/api/public/request-upload', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Agent attaches a document to their own submission anytime (no staff request needed).
  uploadSubmissionDoc(input: { code: string; pin: string; fileName: string; mime: string; data: string }) {
    return request<{ ok: true }>('/api/public/submission-upload', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Agent corrects the insured/business details on their own submission anytime.
  updateSubmission(input: { code: string; pin: string; patch: Record<string, string> }) {
    return request<{ ok: true; changed: number }>('/api/public/submission-update', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  sendMgaEmail(input: {
    submissionId: string;
    to: string;
    subject: string;
    body: string;
    includePaths?: string[];
    extraFiles?: { name: string; mime: string; data: string }[];
  }) {
    return request<{ ok: true; emailed: boolean; attached: number; skipped: string[] }>(
      '/api/staff/send-mga-email',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  },
};
